import { ApplicationStatus } from '@api/btlApplicationForm/types';
import { CaseSidebarItemId, CaseSidebarSection, SectionPagesMap, StatusSidebarChange } from '../types';

/**
 * Happy-path sidebar is built by accumulating deltas along STATUS_CHAIN.
 * Display order within each section comes from SECTION_ITEM_ORDER (sectionOrder.ts), not from add order.
 *
 * Expected result per chain step (matches BTL screenshots):
 *
 * NEW
 *   APPLICATION: Financing, Applicants, Product, Top Slicing*, Affordability, Submit DIP
 *
 * DIP_SUBMITTED
 *   APPLICATION: Financing, Applicants, Product, Top Slicing*, Affordability
 *
 * DIP_ACCEPTED
 *   DOCUMENTS: Customer files, Evidence
 *   APPLICATION: Financing, Applicants, Product, Property, Top Slicing*, Affordability, Solicitors, Bank details, Submit application
 *   OTHER: Communication
 *
 * APPLICATION_SUBMITTED
 *   same as DIP_ACCEPTED minus Submit application
 *
 * FEES_PAID / REVIEWING_APPLICATION / OFFER_ISSUED
 *   same as APPLICATION_SUBMITTED + Valuation
 *
 * OFFER_ACCEPTED / COMPLETION
 *   same as OFFER_ISSUED + Post Offer
 *
 * *Top Slicing only when application.isTopSlicing === true (filtered in itemVisibility)
 *
 * Branch overrides (not on chain): DIP_DECLINED, DIP_EXPIRED — see STATUS_SIDEBAR_OVERRIDE
 */

const CORE_APPLICATION_ITEMS: CaseSidebarItemId[] = [
  CaseSidebarItemId.FINANCING,
  CaseSidebarItemId.APPLICANTS,
  CaseSidebarItemId.PRODUCT,
  CaseSidebarItemId.TOP_SLICING,
  CaseSidebarItemId.AFFORDABILITY_CHECK,
];

const DOCUMENTS_ITEMS: CaseSidebarItemId[] = [CaseSidebarItemId.CUSTOMER_FILES, CaseSidebarItemId.EVIDENCE];

const DIP_ACCEPTED_APPLICATION_ITEMS: CaseSidebarItemId[] = [
  CaseSidebarItemId.PROPERTY,
  CaseSidebarItemId.SOLICITORS,
  CaseSidebarItemId.BANK_DETAILS,
  CaseSidebarItemId.SUBMIT_APPLICATION,
];

export const STATUS_CHAIN: ApplicationStatus[] = [
  ApplicationStatus.NEW,
  ApplicationStatus.DIP_SUBMITTED,
  ApplicationStatus.DIP_ACCEPTED,
  ApplicationStatus.APPLICATION_SUBMITTED,
  ApplicationStatus.FEES_PAID,
  ApplicationStatus.REVIEWING_APPLICATION,
  ApplicationStatus.OFFER_ISSUED,
  ApplicationStatus.OFFER_ACCEPTED,
  ApplicationStatus.COMPLETION,
];

export const STATUS_SIDEBAR_CHANGES: Partial<Record<ApplicationStatus, StatusSidebarChange>> = {
  [ApplicationStatus.NEW]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [...CORE_APPLICATION_ITEMS, CaseSidebarItemId.SUBMIT_DIP],
    },
  },
  [ApplicationStatus.DIP_SUBMITTED]: {
    remove: [CaseSidebarItemId.SUBMIT_DIP],
  },
  [ApplicationStatus.DIP_ACCEPTED]: {
    add: {
      [CaseSidebarSection.DOCUMENTS]: DOCUMENTS_ITEMS,
      [CaseSidebarSection.APPLICATION]: DIP_ACCEPTED_APPLICATION_ITEMS,
      [CaseSidebarSection.OTHER]: [CaseSidebarItemId.COMMUNICATION],
    },
  },
  [ApplicationStatus.APPLICATION_SUBMITTED]: {
    remove: [CaseSidebarItemId.SUBMIT_APPLICATION],
  },
  [ApplicationStatus.FEES_PAID]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.VALUATION],
    },
  },
  [ApplicationStatus.OFFER_ACCEPTED]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.POST_OFFER],
    },
  },
};

const DIP_DECLINED_SIDEBAR: SectionPagesMap = {
  [CaseSidebarSection.DOCUMENTS]: [],
  [CaseSidebarSection.APPLICATION]: [
    ...CORE_APPLICATION_ITEMS,
    CaseSidebarItemId.SOLICITORS,
    CaseSidebarItemId.BANK_DETAILS,
  ],
  [CaseSidebarSection.OTHER]: [CaseSidebarItemId.ADDITIONAL_INFORMATION],
};

export const STATUS_SIDEBAR_OVERRIDE: Partial<Record<ApplicationStatus, SectionPagesMap>> = {
  [ApplicationStatus.DIP_DECLINED]: DIP_DECLINED_SIDEBAR,
  [ApplicationStatus.DIP_EXPIRED]: DIP_DECLINED_SIDEBAR,
};

export const STATUS_TO_CHAIN_STATUS: Partial<Record<ApplicationStatus, ApplicationStatus>> = {
  [ApplicationStatus.REVIEWING_DIP]: ApplicationStatus.DIP_SUBMITTED,
  [ApplicationStatus.DIP_REFERRED]: ApplicationStatus.DIP_SUBMITTED,
  [ApplicationStatus.AWAITING_PROPERTY]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PROPERTY_SUBMITTED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.BOOKING_PRE_ADVICE]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PRE_ADVICE_BOOKED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.ADVICE_BOOKED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PRODUCT_NOT_SUITABLE]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.BTL_APPLICATION_SUBMITTED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.RECOMMENDED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.FIS_ISSUED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.PAYMENT_DUE]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.PROPERTY_DECLINED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.OFFER_BEING_REVISED]: ApplicationStatus.OFFER_ISSUED,
  [ApplicationStatus.OFFER_EXPIRED]: ApplicationStatus.OFFER_ISSUED,
  [ApplicationStatus.APPLICATION_DECLINED]: ApplicationStatus.OFFER_ACCEPTED,
};
